import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Logo, NamedLink, MegaMenu, ExternalLink } from '../../components';
import css from './TopbarDesktop.module.css';
import { NAV_MENUS, navMenuConfig } from '../../util/configHelpers';
import { t } from '../../util/messages';

/**
 * Desktop navigation — a floating glass pill that overlays the hero.
 *
 * Blog posts for the Insights panel are no longer fetched here. `MegaMenu`
 * pulls them from the cached `/api/blog/posts` endpoint via `useLatestPosts`,
 * so the request happens once per page load instead of on every topbar mount,
 * the HTML from the CMS is stripped server-side, and an outage degrades the
 * menu to its static links rather than leaving it empty.
 */

/** Delay before opening, so sweeping across the bar does not flash panels. */
const OPEN_INTENT_MS = 80;
/** Grace period to cross the gap between the pill and the panel. */
const CLOSE_GRACE_MS = 180;

const Chevron = () => (
  <svg className={css.chevron} width="9" height="6" viewBox="0 0 9 6" fill="none" aria-hidden="true">
    <path d="M1 1l3.5 3.5L8 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const TopbarDesktop = ({ className = '', rootClassName = '', config }) => {
  const [topbarHeight, setTopbarHeight] = useState(0);
  const [activeMenu, setActiveMenu] = useState(null);
  const topbarRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    const updateTopbarHeight = () => {
      if (topbarRef.current) setTopbarHeight(topbarRef.current.offsetHeight);
    };
    updateTopbarHeight();
    window.addEventListener('resize', updateTopbarHeight);
    return () => window.removeEventListener('resize', updateTopbarHeight);
  }, []);

  // Never leave a timer running after unmount.
  useEffect(() => () => clearTimeout(timerRef.current), []);

  const openMenu = useCallback(menuId => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setActiveMenu(menuId), OPEN_INTENT_MS);
  }, []);

  const scheduleClose = useCallback(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setActiveMenu(null), CLOSE_GRACE_MS);
  }, []);

  const closeNow = useCallback(() => {
    clearTimeout(timerRef.current);
    setActiveMenu(null);
  }, []);

  // Escape closes the panel — expected of any menu, and required for keyboards.
  useEffect(() => {
    if (!activeMenu) return undefined;
    const onKeyDown = event => {
      if (event.key === 'Escape') closeNow();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [activeMenu, closeNow]);

  const classes = classNames(rootClassName || css.root, className);

  const navItems = [
    /* A route, not a hash: Services now has a page of its own. */
    { id: NAV_MENUS.services, label: t('Topbar.services'), routeName: 'ServicesPage' },
    { id: NAV_MENUS.industries, label: t('Topbar.industries'), hash: '#contact-us' },
    { id: NAV_MENUS.clients, label: t('Topbar.work'), hash: '#portfolio' },
    { id: NAV_MENUS.insights, label: t('Topbar.insights'), url: 'https://blog.gitribe.com' },
  ];

  return (
    <nav className={classes} ref={topbarRef} aria-label="Main navigation">
      <div className={css.container}>
        <div className={css.bar}>
          <NamedLink className={css.home} name="LandingPage" aria-label="Gitribe home">
            <Logo
              format="desktop"
              width={150}
              className={css.sitelogo}
              alt={t('TopbarDesktop.logo')}
            />
          </NamedLink>

          <div className={css.topbarContent}>
            <ul className={css.linksCollection} role="list">
              {navItems.map(item => {
                const isOpen = activeMenu === item.id;
                const panelId = `megamenu-${item.id}`;

                return (
                  <li
                    key={item.id}
                    className={classNames(css.link, { [css.linkOpen]: isOpen })}
                    onMouseEnter={() => openMenu(item.id)}
                    onMouseLeave={scheduleClose}
                  >
                    {item.routeName ? (
                      <NamedLink
                        name={item.routeName}
                        title={item.label}
                        aria-expanded={isOpen}
                        aria-controls={panelId}
                        onClick={closeNow}
                      >
                        {item.label}
                        <Chevron />
                      </NamedLink>
                    ) : item.hash ? (
                      <NamedLink
                        name="LandingPage"
                        title={item.label}
                        to={{ hash: item.hash }}
                        aria-expanded={isOpen}
                        aria-controls={panelId}
                        onClick={closeNow}
                      >
                        {item.label}
                        <Chevron />
                      </NamedLink>
                    ) : (
                      <ExternalLink
                        title={item.label}
                        href={item.url}
                        aria-expanded={isOpen}
                        aria-controls={panelId}
                        onClick={closeNow}
                      >
                        {item.label}
                        <Chevron />
                      </ExternalLink>
                    )}

                    <MegaMenu
                      id={panelId}
                      className={classNames(css.megaMenu, { [css.megaMenuOpen]: isOpen })}
                      config={navMenuConfig(item.id, config)}
                      topbarHeight={topbarHeight}
                      onLinkClick={closeNow}
                    />
                  </li>
                );
              })}
            </ul>

            <div className={css.actions}>
              <NamedLink
                className={css.contact}
                name="LandingPage"
                title={t('Topbar.startProject')}
                to={{ hash: '#contact-us' }}
              >
                {t('Topbar.startProject')}
              </NamedLink>
            </div>
          </div>
        </div>
      </div>

      {/* Dims the page behind an open panel, so it reads as sitting above. */}
      <div
        className={classNames(css.scrim, { [css.scrimVisible]: Boolean(activeMenu) })}
        onClick={closeNow}
        aria-hidden="true"
      />
    </nav>
  );
};

const { string, object } = PropTypes;

TopbarDesktop.propTypes = {
  rootClassName: string,
  className: string,
  config: object,
};

export default TopbarDesktop;
